import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Button, Paper, Text } from '@ltvco/refresh-lib/theme';
import { useContext, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { useCallerIdSmokeTest } from './CallerIdSmokeTestContext';
import { CALLER_ID_COPY } from './types';

/**
 * Inline Smart Caller ID card for mobile phone / fraud reports (RFRSH-4427).
 */
export function CallerIdInlineCard(): JSX.Element | null {
  const { isExperienceActive, isMobile, openComingSoon, gaEventBase } =
    useCallerIdSmokeTest();
  const { trackEventGA4 } = useContext(AppConfig);
  const impressionLogged = useRef(false);
  const [hasSubmittedGA4, setHasSubmittedGA4] = useState(false);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.25,
  });

  useEffect(() => {
    if (
      !isExperienceActive ||
      !isMobile ||
      !inView ||
      impressionLogged.current ||
      hasSubmittedGA4
    ) {
      return;
    }
    impressionLogged.current = true;
    trackEventGA4({
      ...gaEventBase,
      event_name: 'caller_id_cta_impression',
      cta_variant: 'fraud_scores',
      placement: 'inline',
    });
    setHasSubmittedGA4(true);
  }, [
    gaEventBase,
    hasSubmittedGA4,
    inView,
    isExperienceActive,
    isMobile,
    trackEventGA4,
  ]);

  if (!isExperienceActive || !isMobile) {
    return null;
  }

  const copy = CALLER_ID_COPY.fraud_scores;

  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={(theme) => ({
        mx: 2,
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Text variant="body2" sx={{ mb: 1.5 }}>
        {copy.body}
      </Text>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={() => openComingSoon('fraud_scores', 'inline')}
      >
        {copy.cta}
      </Button>
    </Paper>
  );
}
